import { useState } from "react";
import { AlertTriangle, Lock, ShieldCheck, User } from "lucide-react";
import { Button, Field, Input, Spinner } from "@discordforge/ui";

export function AdminLoginScreen({ onLogin }: { onLogin: (login: string, password: string) => Promise<void> }) {
    const [login, setLogin] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!login.trim() || !password || busy) return;
        setBusy(true);
        setError("");
        try {
            await onLogin(login.trim(), password);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Login failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="relative flex min-h-screen items-center justify-center bg-[#0b0b0f] p-6 text-white">
            <div className="pointer-events-none fixed -top-40 right-0 h-[480px] w-[480px] rounded-full bg-orange-500/10 blur-[140px]" />
            <div className="pointer-events-none fixed -bottom-40 left-0 h-[480px] w-[480px] rounded-full bg-amber-500/5 blur-[140px]" />

            <div className="relative w-full max-w-md">
                <div className="mb-8 flex flex-col items-center gap-3 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-orange-500/20 bg-orange-500/10 text-orange-400 shadow-[0_0_30px_rgba(249,115,22,0.2)]">
                        <ShieldCheck size={26} />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black tracking-tighter">
                            DiscordForge <span className="forge-gradient">Admin</span>
                        </h1>
                        <p className="mt-1 text-sm font-medium text-zinc-400">
                            Manage accounts and webmail configuration
                        </p>
                    </div>
                </div>

                <div className="relative overflow-hidden rounded-[32px] border border-white/10 bg-white/[0.03] p-8 shadow-2xl backdrop-blur-xl">
                    <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-orange-500/10 blur-[100px]" />
                    <form onSubmit={submit} className="relative flex flex-col gap-5">
                        <Field label="Admin login" htmlFor="admin-login" required>
                            <div className="relative">
                                <User size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                                <Input id="admin-login" autoComplete="username" placeholder="admin" value={login} onChange={(e) => setLogin(e.target.value)} className="pl-10" />
                            </div>
                        </Field>
                        <Field label="Password" htmlFor="admin-password" required>
                            <div className="relative">
                                <Lock size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                                <Input id="admin-password" type="password" autoComplete="current-password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10" />
                            </div>
                        </Field>

                        {error && (
                            <div className="flex items-start gap-2.5 rounded-xl border border-red-500/25 bg-red-500/10 px-3.5 py-2.5 text-xs font-medium text-red-400">
                                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                {error}
                            </div>
                        )}

                        <Button type="submit" size="lg" disabled={busy} className="mt-1 w-full">
                            {busy ? <Spinner size={16} /> : "Sign in to admin"}
                        </Button>
                    </form>
                </div>

                <p className="mt-6 text-center text-xs text-zinc-500">
                    Admin session is separate from user mailboxes · TOTP supported
                </p>
            </div>
        </div>
    );
}
